import type { OverrideOptionInput } from '@erp/rpc';
import { multiplyMoney } from '@erp/shared-utils';
import type { AssignmentLine } from 'src/api/rpc/hooks/procurement/useReplenishmentSuggestions';

type EditableAction = Exclude<
  NonNullable<AssignmentLine['topOption']>['action'],
  'stock'
>;

const actionFlags: Record<
  EditableAction,
  { usesBuy: boolean; usesTransfer: boolean }
> = {
  buy: { usesBuy: true, usesTransfer: false },
  transfer: { usesBuy: false, usesTransfer: true },
  buy_and_transfer: { usesBuy: true, usesTransfer: true },
};

type QuantityOverrideResult =
  | { kind: 'save'; input: OverrideOptionInput }
  | { kind: 'noop' }
  | { kind: 'missing-context' };

export function calculateDraftCost({
  draftQuantity,
  unitCost,
  currentCost,
}: {
  draftQuantity: string | null;
  unitCost: number | null;
  currentCost: number;
}): number | string {
  if (draftQuantity == null || unitCost == null) return currentCost;

  const quantity = Number(draftQuantity);
  if (!Number.isFinite(quantity) || quantity < 0) return currentCost;

  return multiplyMoney(unitCost, quantity).toString();
}

function parseChangedQuantity(
  value: string,
  currentQuantity: number,
): number | null {
  const quantity = Number(value);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (quantity === currentQuantity) return null;
  return quantity;
}

function resolveRequiredId(
  required: boolean,
  value: string | null | undefined,
): { valid: boolean; value: string | null } {
  if (!required) return { valid: true, value: null };
  return value == null ? { valid: false, value: null } : { valid: true, value };
}

export function buildQuantityOverride({
  line,
  value,
  supplierId,
  sourceWarehouseId,
}: {
  line: AssignmentLine;
  value: string;
  supplierId?: string;
  sourceWarehouseId?: string;
}): QuantityOverrideResult {
  const option = line.topOption;
  if (line.status !== 'pending') return { kind: 'noop' };
  if (
    option == null ||
    option.action === 'stock' ||
    option.planKind !== 'direct'
  ) {
    return { kind: 'noop' };
  }

  const currentQuantity =
    option.buyQuantity ?? option.transferQuantity ?? line.neededQuantity;
  const quantity = parseChangedQuantity(value, currentQuantity);
  if (quantity == null) return { kind: 'noop' };
  const { action } = option;
  return planAtQuantity(line, { ...option, action }, quantity, {
    supplierId,
    sourceWarehouseId,
  });
}

/**
 * The plan for a new stock line taking a need's overflow (GEN-2938): `line`'s
 * plan (vendor, ship-to, source) at `quantity`. The override names `line`;
 * createOverflowLine adds the new line at its item and location.
 */
export function buildOverflowLineInput({
  line,
  quantity,
  supplierId,
  sourceWarehouseId,
}: {
  line: AssignmentLine;
  quantity: number;
  supplierId?: string;
  sourceWarehouseId?: string;
}): QuantityOverrideResult {
  const option = line.topOption;
  if (line.status !== 'pending') return { kind: 'noop' };
  if (option == null || option.action === 'stock') return { kind: 'noop' };
  if (!(quantity > 0)) return { kind: 'noop' };
  const { action } = option;
  return planAtQuantity(line, { ...option, action }, quantity, {
    supplierId,
    sourceWarehouseId,
  });
}

function planAtQuantity(
  line: AssignmentLine,
  option: NonNullable<AssignmentLine['topOption']> & { action: EditableAction },
  quantity: number,
  {
    supplierId,
    sourceWarehouseId,
  }: { supplierId?: string; sourceWarehouseId?: string },
): QuantityOverrideResult {
  const flags = actionFlags[option.action];
  const supplier = resolveRequiredId(flags.usesBuy, supplierId);
  const source = resolveRequiredId(
    flags.usesTransfer,
    option.sourceWarehouseId ?? sourceWarehouseId,
  );
  if (!supplier.valid || !source.valid) return { kind: 'missing-context' };

  return {
    kind: 'save',
    input: {
      suggestionId: line.suggestionId,
      action: option.action,
      supplierId: supplier.value,
      shipToWarehouseId: flags.usesBuy ? line.shipToWarehouseId : null,
      buyQuantity: flags.usesBuy ? quantity : null,
      unitCost: flags.usesBuy ? option.unitCost : null,
      sourceWarehouseId: source.value,
      transferQuantity: flags.usesTransfer ? quantity : null,
      setChosen: true,
    },
  };
}
